/**
 * QuizPaymentStep — Phase 1 manual-capture checkout flow.
 *
 * Loads inside the quiz funnel after the patient has reviewed their
 * recommended protocol. Renders Stripe Payment Element (card + Apple/Google
 * Pay) and on confirm, calls our backend `/api/public/quiz-checkout` which
 * creates a manual-capture PaymentIntent — the patient's card is AUTHORIZED
 * but NOT charged. A doctor reviews their medical history within 5 days; on
 * approve we capture the auth, on decline we cancel it.
 *
 * Why two-step (not Stripe Checkout hosted page)? We need the PaymentIntent
 * id back synchronously so we can capture it later. Hosted Checkout doesn't
 * support `capture_method=manual`.
 */
import { useState, useEffect, useMemo } from "react";
import { loadStripe } from "@stripe/stripe-js";
import {
  Elements,
  PaymentElement,
  useStripe,
  useElements,
} from "@stripe/react-stripe-js";
import { motion } from "framer-motion";
import { ShieldCheck, Loader2, CheckCircle2, Lock } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const STRIPE_PK = process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY;
const stripePromise = STRIPE_PK ? loadStripe(STRIPE_PK) : null;

// ─── Inner form (rendered inside <Elements>) ─────────────────
// Map raw Stripe error codes to friendlier patient-facing copy.
function mapStripeError(err) {
  if (!err) return null;
  const code = err.code || err.decline_code || "";
  const map = {
    card_declined: "Your card was declined. Try a different card or contact your bank.",
    insufficient_funds: "Insufficient funds on this card. Try another card.",
    expired_card: "This card has expired. Please use a different card.",
    incorrect_cvc: "The security code (CVC) is incorrect.",
    incorrect_zip: "The billing ZIP code didn't match. Double-check and retry.",
    processing_error: "A processing error occurred — please try again in a moment.",
    incorrect_number: "The card number is invalid. Re-enter it carefully.",
    invalid_expiry_month: "The expiration month is invalid.",
    invalid_expiry_year: "The expiration year is invalid.",
    fraudulent: "Your bank flagged this transaction. Please call them or try a different card.",
    do_not_honor: "Your bank declined this charge. Please contact them or try a different card.",
    generic_decline: "Your bank declined this charge. Please try a different card.",
    lost_card: "This card was reported lost. Please use a different card.",
    stolen_card: "This card was reported stolen. Please use a different card.",
  };
  return map[code] || err.message || "Card couldn't be authorized. Try another card.";
}

const SUBMIT_STAGES = [
  { id: "validate", label: "Validating your card details…" },
  { id: "authorize", label: "Authorizing $0 — no charge yet…" },
  { id: "queue", label: "Queueing your file for the doctor…" },
];

function CheckoutForm({ payload, recommendedPeptides, onSuccess, monthlyTotal }) {
  const stripe = useStripe();
  const elements = useElements();
  const [submitting, setSubmitting] = useState(false);
  const [stageIdx, setStageIdx] = useState(0);
  const [error, setError] = useState(null);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!stripe || !elements) return;
    setError(null);
    setSubmitting(true);
    setStageIdx(0);
    try {
      // 1. Validate the form via Stripe (catches missing fields immediately).
      const { error: submitError } = await elements.submit();
      if (submitError) {
        setError(mapStripeError(submitError));
        setSubmitting(false);
        return;
      }
      setStageIdx(1);
      // 2. Create a payment_method from the form (no charge yet).
      const pmRes = await stripe.createPaymentMethod({ elements });
      if (pmRes.error) {
        setError(mapStripeError(pmRes.error));
        setSubmitting(false);
        return;
      }
      // 3. POST to our backend — it creates the PaymentIntent (manual capture)
      //    and returns client_secret if 3DS is required.
      setStageIdx(2);
      const { data } = await axios.post(`${API}/public/quiz-checkout`, {
        ...payload,
        payment_method_id: pmRes.paymentMethod.id,
      });
      if (!data.ok) {
        throw new Error(data.message || "Checkout failed");
      }
      // 4. If the PaymentIntent requires action (3DS), confirm it with Stripe.
      if (data.payment_intent_status === "requires_action" && data.client_secret) {
        const conf = await stripe.confirmCardPayment(data.client_secret);
        if (conf.error) {
          setError(mapStripeError(conf.error));
          setSubmitting(false);
          return;
        }
      }
      onSuccess(data);
    } catch (e2) {
      // Backend error (HTTP) — surface server detail; fall back to network message.
      let msg = e2.response?.data?.detail;
      if (!msg) {
        if (e2?.message?.toLowerCase().includes("network")) {
          msg = "Can't reach the server. Check your internet and try again.";
        } else if (e2.response?.status === 429) {
          msg = "Too many attempts. Please wait a minute and try again.";
        } else if (e2.response?.status >= 500) {
          msg = "Our server hiccuped. Please retry in a moment.";
        } else {
          msg = e2.message || "Something went wrong. Try a different card?";
        }
      }
      setError(msg);
      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} data-testid="quiz-checkout-form" className="space-y-6">
      <div className="bg-[#F7F5F0] rounded-2xl p-5 border border-[#2A3324]/5">
        <div className="flex items-start gap-3 mb-3">
          <ShieldCheck size={20} className="text-emerald-700 shrink-0 mt-0.5" />
          <div className="text-sm font-sans text-[#2A3324]/80 leading-relaxed">
            <strong className="text-[#2A3324]">Authorization only — you won't be charged yet.</strong> A
            licensed physician will review your medical history within 24 hours
            (5-day max). Your card is only charged after approval. If declined,
            the authorization is automatically released.
          </div>
        </div>
      </div>

      <div className="rounded-2xl border border-[#2A3324]/10 p-4">
        <PaymentElement options={{ layout: "tabs" }} />
      </div>

      {error && (
        <div data-testid="quiz-checkout-error" className="text-sm font-sans text-red-700 bg-red-50 border border-red-200 rounded-xl p-3">
          {error}
        </div>
      )}

      <button
        type="submit"
        data-testid="quiz-checkout-submit"
        disabled={!stripe || submitting}
        className="w-full bg-[#5C715E] hover:bg-[#4A5A4B] text-[#F7F5F0] font-sans font-medium text-base md:text-lg rounded-2xl px-6 py-4 transition-all disabled:opacity-60 flex items-center justify-center gap-2"
      >
        {submitting ? (
          <>
            <Loader2 size={18} className="animate-spin" /> {SUBMIT_STAGES[stageIdx].label}
          </>
        ) : (
          <>
            <Lock size={16} />
            Start Treatment — ${monthlyTotal.toFixed(0)}/mo
          </>
        )}
      </button>

      {submitting && (
        <div data-testid="quiz-checkout-progress" className="space-y-1.5 -mt-2">
          {SUBMIT_STAGES.map((s, i) => (
            <div key={s.id} className="flex items-center gap-2 text-[11px] font-sans">
              {i < stageIdx ? (
                <CheckCircle2 size={12} className="text-emerald-600" />
              ) : i === stageIdx ? (
                <Loader2 size={12} className="text-[#5C715E] animate-spin" />
              ) : (
                <span className="w-3 h-3 rounded-full border border-[#2A3324]/20" />
              )}
              <span className={i <= stageIdx ? "text-[#2A3324]/70" : "text-[#2A3324]/30"}>{s.label}</span>
            </div>
          ))}
        </div>
      )}

      <p className="text-[11px] text-center text-[#2A3324]/40 font-sans">
        Secured by Stripe. We never see your card details.
      </p>
    </form>
  );
}

// ─── Outer wrapper: builds the Elements provider + monthly preview ──
export default function QuizPaymentStep({
  recommendedPeptides,
  contactInfo,
  answers,
  onSuccess,
}) {
  const [monthlyTotal, setMonthlyTotal] = useState(null);
  const [pricingError, setPricingError] = useState(null);
  const [loadingPrice, setLoadingPrice] = useState(true);

  // Fetch the live monthly total via /api/pricing/preview (uses the dynamic
  // pricing engine — no hardcoded numbers, never drifts from server).
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const peptidePayload = recommendedPeptides.map((p) => ({
          name: p.name,
          term_cycles: 3,
          dosage: p.dosage || "",
          frequency: p.frequency || "Weekly",
        }));
        const { data } = await axios.post(`${API}/pricing/preview`, {
          assigned_peptides: peptidePayload,
          cadence: "monthly",
          use_dynamic_pricing: true,
        });
        if (!cancelled) {
          setMonthlyTotal(data?.monthly_total ?? data?.total ?? 0);
          setLoadingPrice(false);
        }
      } catch (e) {
        if (!cancelled) {
          setPricingError(
            e.response?.data?.detail ||
              "Couldn't compute your protocol price. Please try again."
          );
          setLoadingPrice(false);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [recommendedPeptides]);

  const stripeOptions = useMemo(() => {
    if (!monthlyTotal || monthlyTotal <= 0) return null;
    return {
      mode: "payment",
      amount: Math.round(monthlyTotal * 100),
      currency: "usd",
      capture_method: "manual",
      paymentMethodCreation: "manual",
      appearance: {
        theme: "flat",
        variables: {
          colorPrimary: "#5C715E",
          colorBackground: "#FFFFFF",
          colorText: "#2A3324",
          fontFamily: "system-ui, sans-serif",
          borderRadius: "12px",
        },
      },
    };
  }, [monthlyTotal]);

  // If Stripe.js itself fails to load (ad-blocker, CSP, network issue), drop to
  // the same hosted-checkout handoff path we use when the publishable key is
  // missing entirely. Tested by polling stripePromise; on resolution failure
  // OR a timeout, flip to fallback.
  const [stripeJsFailed, setStripeJsFailed] = useState(false);
  useEffect(() => {
    if (!stripePromise) return;
    let cancelled = false;
    const timeout = setTimeout(() => {
      if (!cancelled) setStripeJsFailed(true);  // 8s grace for slow connections
    }, 8000);
    stripePromise.then(
      (s) => { if (!cancelled) { clearTimeout(timeout); if (!s) setStripeJsFailed(true); } },
      () => { if (!cancelled) { clearTimeout(timeout); setStripeJsFailed(true); } }
    );
    return () => { cancelled = true; clearTimeout(timeout); };
  }, []);

  if (!STRIPE_PK || stripeJsFailed) {
    return (
      <QuizHandoffFallback
        contactInfo={contactInfo}
        answers={answers}
        recommendedPeptides={recommendedPeptides}
        onSuccess={onSuccess}
      />
    );
  }
  if (loadingPrice) {
    return (
      <div data-testid="quiz-checkout-loading" className="flex items-center justify-center py-12 text-[#2A3324]/60">
        <Loader2 size={24} className="animate-spin mr-2" /> Building your treatment plan…
      </div>
    );
  }
  if (pricingError) {
    return <div className="text-sm text-red-700 bg-red-50 rounded-xl p-4">{pricingError}</div>;
  }

  // Build the payload that we'll submit AFTER Stripe collects the payment_method.
  const checkoutPayload = {
    first_name: contactInfo.first_name,
    last_name: contactInfo.last_name,
    email: contactInfo.email,
    phone: contactInfo.phone,
    state: contactInfo.state || answers?.state?.[0] || "",
    date_of_birth: contactInfo.date_of_birth || null,
    quiz_answers: answers,
    recommended_peptides: recommendedPeptides.map((p) => p.name),
    shipping_address: contactInfo.shipping_address || null,
    accepts_telehealth_consent: true,
    accepts_terms: true,
  };

  return (
    <div data-testid="quiz-payment-step">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4 }}
        className="mb-6"
      >
        <span className="text-xs md:text-sm font-sans font-medium uppercase tracking-[0.2em] text-[#C47C5D] mb-3 block">
          Final Step
        </span>
        <h3 data-testid="quiz-checkout-heading" className="text-2xl md:text-3xl font-serif font-light tracking-tight text-[#2A3324]">
          Reserve your protocol — ${monthlyTotal.toFixed(0)}/mo
        </h3>
        <p className="mt-2 text-sm text-[#2A3324]/60 font-sans">
          Doctor consult within 24 hours. Free overnight shipping. Cancel anytime.
        </p>
      </motion.div>

      <Elements stripe={stripePromise} options={stripeOptions}>
        <CheckoutForm
          payload={checkoutPayload}
          recommendedPeptides={recommendedPeptides}
          monthlyTotal={monthlyTotal}
          onSuccess={onSuccess}
        />
      </Elements>
    </div>
  );
}

// ─── Fallback when inline Stripe Element can't load ─────────
// Triggers /api/public/quiz-handoff which creates the applicant + texts/emails
// the patient a Stripe-hosted checkout link. UX is identical to the success
// flow — patient sees "Almost done!" and a confirmation that the link was sent
// to their phone. Production-ready substitute when the publishable key isn't
// in the build, an ad-blocker is killing Stripe.js, or we're in a PWA.
function QuizHandoffFallback({ contactInfo, answers, recommendedPeptides, onSuccess }) {
  const [state, setState] = useState({ status: "submitting", error: null, result: null });

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await axios.post(`${API}/public/quiz-handoff`, {
          first_name: contactInfo.first_name,
          last_name: contactInfo.last_name,
          email: contactInfo.email,
          phone: contactInfo.phone,
          state: contactInfo.state || answers?.state?.[0] || "",
          quiz_answers: answers,
          recommended_peptides: recommendedPeptides.map((p) => p.name),
          shipping_address: contactInfo.shipping_address || null,
          origin_url: window.location.origin,
        });
        if (!cancelled) {
          setState({ status: "ok", error: null, result: data });
        }
      } catch (e) {
        if (!cancelled) {
          setState({
            status: "error",
            error: e.response?.data?.detail || "Couldn't queue your enrollment. Please call us.",
            result: null,
          });
        }
      }
    })();
    return () => { cancelled = true; };
  }, [contactInfo, answers, recommendedPeptides]);

  if (state.status === "submitting") {
    return (
      <div data-testid="quiz-handoff-submitting" className="bg-white rounded-2xl p-8 md:p-10 border border-[#2A3324]/5 text-center shadow-[0_8px_30px_rgb(0,0,0,0.04)]">
        <Loader2 size={28} className="animate-spin text-[#5C715E] mx-auto mb-4" />
        <p className="text-base font-sans text-[#2A3324]/70">Finalizing your enrollment…</p>
      </div>
    );
  }
  if (state.status === "error") {
    return (
      <div data-testid="quiz-handoff-error" className="bg-white rounded-2xl p-8 md:p-10 border border-[#2A3324]/5 text-center shadow-[0_8px_30px_rgb(0,0,0,0.04)]">
        <ShieldCheck size={28} className="text-amber-600 mx-auto mb-4" />
        <h3 className="text-2xl font-serif font-light text-[#2A3324] mb-3">Almost there!</h3>
        <p className="text-sm font-sans text-[#2A3324]/70 leading-relaxed mb-2">
          We saved your information and our care team will reach out shortly to finalize your enrollment.
        </p>
        <p className="text-xs font-sans text-[#2A3324]/50">
          Questions? Text us at <strong>(775) 278-9583</strong>.
        </p>
      </div>
    );
  }

  // Success — applicant created, payment link queued
  const r = state.result || {};
  const phoneSent = r.sent?.sms?.ok;
  const emailSent = r.sent?.email?.ok;
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4 }}
      data-testid="quiz-handoff-success"
      className="bg-white rounded-2xl p-8 md:p-12 border border-[#2A3324]/5 shadow-[0_8px_30px_rgb(0,0,0,0.04)] text-center"
    >
      <div className="w-16 h-16 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-6">
        <CheckCircle2 size={28} strokeWidth={1.7} className="text-emerald-700" />
      </div>
      <h3 className="text-2xl md:text-3xl font-serif font-light text-[#2A3324] mb-4">
        Almost done — check your phone
      </h3>
      <p className="text-sm md:text-base font-sans text-[#2A3324]/70 leading-relaxed mb-2 max-w-md mx-auto">
        We've {phoneSent ? "texted" : ""}{phoneSent && emailSent ? " and emailed" : emailSent ? "emailed" : ""} you a secure link to add your payment method.
      </p>
      <p className="text-sm font-sans text-[#2A3324]/60 mb-6">
        {phoneSent && contactInfo.phone && (
          <>Text sent to <strong className="text-[#2A3324]">{contactInfo.phone}</strong></>
        )}
        {phoneSent && emailSent && <br />}
        {emailSent && (
          <>Email sent to <strong className="text-[#2A3324]">{contactInfo.email}</strong></>
        )}
      </p>

      <div className="bg-[#F7F5F0] border border-[#2A3324]/5 rounded-xl p-4 text-left max-w-md mx-auto mb-6">
        <p className="text-xs font-sans text-[#2A3324]/70 leading-relaxed">
          <strong>Important:</strong> Tapping the link will open Stripe's secure
          checkout page. <strong>You won't be charged yet</strong> — a licensed
          physician will review your medical history within 24 hours, then bill
          your saved card only on approval.
        </p>
      </div>

      {r.payment_url && (
        <a
          data-testid="quiz-handoff-cta"
          href={r.payment_url}
          className="inline-flex items-center gap-2 bg-[#5C715E] hover:bg-[#4A5A4B] text-[#F7F5F0] font-sans font-medium text-base rounded-2xl px-8 py-4 transition-all"
        >
          <Lock size={16} />
          Open secure checkout
        </a>
      )}

      <p className="text-[11px] font-sans text-[#2A3324]/40 mt-6">
        Need help? Text us at <strong>(775) 278-9583</strong>.
      </p>
    </motion.div>
  );
}


// ─── Order received state (shown post-success) ───────────────
export function QuizOrderReceived({ result }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5 }}
      data-testid="quiz-order-received"
      className="bg-white rounded-[2rem] p-8 md:p-14 border border-[#2A3324]/5 shadow-[0_8px_30px_rgb(0,0,0,0.04)] text-center"
    >
      <div className="w-20 h-20 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-8">
        <CheckCircle2 size={36} strokeWidth={1.5} className="text-emerald-700" />
      </div>
      <h2
        data-testid="quiz-order-received-heading"
        className="text-3xl md:text-4xl lg:text-5xl font-serif font-light tracking-tight text-[#2A3324] mb-4"
      >
        Order received
      </h2>
      <p className="text-base md:text-lg font-sans font-light leading-relaxed text-[#2A3324]/60 max-w-xl mx-auto mb-4">
        A licensed physician will review your medical history within 24 hours.
        Your card will only be charged after approval — if declined, the
        authorization is released automatically.
      </p>
      <p className="text-sm font-sans font-light text-[#2A3324]/40 max-w-lg mx-auto">
        We've emailed your account link to <strong>{result?.account_email}</strong>. Check your
        inbox in a few minutes.
      </p>
    </motion.div>
  );
}
